import { useMemo, useState } from 'react';
import type { Permission } from '@ecobox/domain';
import type { OpsContainer } from '@ecobox/firebase';
import { Badge, Button, Card, EmptyState, Field, Input, useToast } from '@ecobox/ui';
import { useExportContainers, useImportContainers } from '../hooks/useOps';
import { containerLabel, containerStatusLabel, containerStatusTone } from '../labels';
import { useBranch, useServices } from '../services';
import { usePermissions } from '../sessions';

type YardType =
  | 'recordEmptyPickup'
  | 'gateInAtPort'
  | 'pullOutContainer'
  | 'arriveAtHub'
  | 'returnEmptyContainer';

interface YardAction {
  type: YardType;
  label: string;
  hint: string;
  permission: Permission;
  /** The container states this step normally follows; the server decides for real. */
  next: readonly string[];
}

export const YARD_ACTIONS: readonly YardAction[] = [
  {
    type: 'recordEmptyPickup',
    label: 'Empty picked up',
    hint: 'The empty container left the depot to be stuffed.',
    permission: 'container.manage',
    next: ['planned'],
  },
  {
    type: 'gateInAtPort',
    label: 'Gated in at the port',
    hint: 'The full container was delivered to the port.',
    permission: 'container.manage',
    next: ['closed'],
  },
  {
    type: 'pullOutContainer',
    label: 'Pulled out of the port',
    hint: 'The full container left the port for the hub.',
    permission: 'container.manage',
    next: ['arrived_port'],
  },
  {
    type: 'arriveAtHub',
    label: 'Arrived at the hub',
    hint: 'The container is at the hub gate. Starts the devanning clock.',
    permission: 'trip.unload',
    next: ['pulled_out'],
  },
  {
    type: 'returnEmptyContainer',
    label: 'Empty returned',
    hint: 'The empty container is back with the shipping line. Stops the detention clock.',
    permission: 'container.manage',
    next: ['devanned'],
  },
];

/** `YYYY-MM-DDTHH:mm` in the phone's own time, the shape a datetime-local box wants. */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function YardPage() {
  const branch = useBranch().current;
  const exportSide = useExportContainers(branch);
  const importSide = useImportContainers(branch);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const containers = useMemo(() => {
    const byId = new Map<string, OpsContainer>();
    for (const c of [...exportSide.data, ...importSide.data]) byId.set(c.id, c);
    const q = filter.trim().toLowerCase();
    return [...byId.values()]
      .filter(
        (c) =>
          q === '' ||
          [c.containerNo, c.bookingNo, c.blNo, c.id].some((v) => v.toLowerCase().includes(q)),
      )
      .sort((a, b) => b.stateSince.localeCompare(a.stateSince));
  }, [exportSide.data, importSide.data, filter]);

  const picked = containers.find((c) => c.id === pickedId) ?? null;
  const error = exportSide.error ?? importSide.error;

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Container yard</h1>
      {picked ? (
        <PickedContainer container={picked} onBack={() => setPickedId(null)} />
      ) : (
        <>
          <div data-tour="yard-find">
            <Field label="Find a container">
              {(p) => (
                <Input
                  {...p}
                  size="touch"
                  value={filter}
                  placeholder="Container, booking or BL number"
                  onChange={(e) => setFilter(e.target.value)}
                />
              )}
            </Field>
          </div>
          {error ? (
            <p role="alert" className="ops-error">
              {error}
            </p>
          ) : null}
          {containers.length === 0 ? (
            <EmptyState
              title={exportSide.loading || importSide.loading ? 'Loading…' : 'No containers found'}
              description={`Containers for ${branch ?? 'your branch'} show here.`}
            />
          ) : (
            <ul className="ops-list" aria-label="Containers" data-tour="yard-list">
              {containers.map((c) => (
                <li key={c.id} className="ops-row">
                  <div className="ops-row__top">
                    <strong>{containerLabel(c)}</strong>
                    <Badge tone={containerStatusTone(c.status)}>
                      {containerStatusLabel(c.status)}
                    </Badge>
                  </div>
                  <Button size="touch" variant="primary" block onClick={() => setPickedId(c.id)}>
                    Record a step
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function PickedContainer({ container, onBack }: { container: OpsContainer; onBack: () => void }) {
  const perms = usePermissions();
  return (
    <>
      <Button size="lg" variant="ghost" onClick={onBack}>
        Choose another container
      </Button>
      <Card title={containerLabel(container)} data-tour="yard-container">
        <Badge tone={containerStatusTone(container.status)}>
          {containerStatusLabel(container.status)}
        </Badge>
      </Card>
      {YARD_ACTIONS.filter((a) => perms.has(a.permission)).map((a) => (
        <YardCard key={a.type} action={a} container={container} />
      ))}
    </>
  );
}

function YardCard({ action, container }: { action: YardAction; container: OpsContainer }) {
  const { writer } = useServices();
  const toast = useToast();
  const [at, setAt] = useState(() => toLocalInput(new Date()));
  const [containerNo, setContainerNo] = useState(container.containerNo);
  const [depot, setDepot] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const suggested = action.next.includes(container.status);

  const record = () => {
    setError(null);
    const when = new Date(at);
    if (Number.isNaN(when.getTime())) return setError('Choose the date and time.');
    if (when.getTime() > Date.now() + 5 * 60_000) return setError('That time is in the future.');
    const iso = when.toISOString();
    const id = container.id;
    try {
      switch (action.type) {
        case 'recordEmptyPickup':
          if (containerNo.trim() === '') return setError('Enter the container number.');
          writer.send('recordEmptyPickup', {
            containerId: id,
            containerNo: containerNo.trim(),
            at: iso,
          });
          break;
        case 'returnEmptyContainer':
          writer.send('returnEmptyContainer', { containerId: id, at: iso, depot: depot.trim() });
          break;
        default:
          writer.send(action.type, { containerId: id, at: iso });
      }
      setDone(iso);
      toast.show({
        title: action.label,
        message: 'Saved. It sends when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Card title={action.label} className={suggested ? 'ops-card--next' : undefined}>
      <div className="ops-stack">
        <p className="ops-muted" style={{ margin: 0 }}>
          {action.hint}
          {suggested ? ' This is the usual next step.' : ''}
        </p>
        {action.type === 'recordEmptyPickup' ? (
          <Field label="Container number" required>
            {(p) => (
              <Input
                {...p}
                size="touch"
                value={containerNo}
                maxLength={20}
                onChange={(e) => setContainerNo(e.target.value.toUpperCase())}
              />
            )}
          </Field>
        ) : null}
        {action.type === 'returnEmptyContainer' ? (
          <Field label="Depot (optional)">
            {(p) => (
              <Input
                {...p}
                size="touch"
                value={depot}
                maxLength={80}
                onChange={(e) => setDepot(e.target.value)}
              />
            )}
          </Field>
        ) : null}
        <Field label="When" error={error}>
          {(p) => (
            <Input
              {...p}
              size="touch"
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
            />
          )}
        </Field>
        <Button size="touch" variant={suggested ? 'cta' : 'secondary'} block onClick={record}>
          Record: {action.label}
        </Button>
        {done ? (
          <p role="status" className="ops-ok" style={{ margin: 0 }}>
            Recorded for {new Date(done).toLocaleString()}.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
